import * as Sentry from '@sentry/react'
import axios from 'axios'
import { useEffect, useRef } from 'react'

import { Anchor } from '@/components/ui/Anchor'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { isProduction, SUPPORT_EMAIL } from '../config/config'
import { useSignOut } from '../models/auth'

const AUTO_RETRY_MS = 30_000

function isAuthError(error: unknown): boolean {
  return axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)
}

function errorDetails(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    const url = error.config?.url
    return [error.message, status && `HTTP ${status}`, url].filter(Boolean).join(' — ')
  }
  return error instanceof Error ? error.message : String(error)
}

interface AppLoadErrorProps {
  error: unknown
  onRetry: () => void
  retrying?: boolean
}

/**
 * Full-page state for when the signed-in user can't be loaded, so a failed
 * bootstrap never reads as an endless spinner. Auth failures offer sign-in;
 * everything else retries (button + background interval).
 */
export function AppLoadError({ error, onRetry, retrying }: AppLoadErrorProps) {
  const signOut = useSignOut()
  const authError = isAuthError(error)

  useEffect(() => {
    // Expired sessions are an expected flow, not an incident worth paging on.
    if (!isAuthError(error)) Sentry.captureException(error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Ref so rerenders (callers pass inline callbacks) never reset the countdown.
  const onRetryRef = useRef(onRetry)
  onRetryRef.current = onRetry

  useEffect(() => {
    if (authError) return
    const interval = setInterval(() => onRetryRef.current(), AUTO_RETRY_MS)
    return () => clearInterval(interval)
  }, [authError])

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>{authError ? 'Your session has expired' : "We couldn't load your account"}</CardTitle>
          <CardDescription>
            {authError
              ? 'Sign in again to keep working.'
              : 'Something went wrong on our side. Your data is safe.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!authError && (
            <p className="text-muted-foreground text-sm">
              We'll keep trying automatically. If this keeps happening, email{' '}
              <Anchor href={`mailto:${SUPPORT_EMAIL}`} className="underline">
                {SUPPORT_EMAIL}
              </Anchor>
              .
            </p>
          )}
          {!isProduction && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs">
              <div className="mb-1 font-semibold text-amber-700">
                Technical details — hidden in production
              </div>
              <code className="break-all text-amber-900">{errorDetails(error)}</code>
            </div>
          )}
        </CardContent>
        <CardFooter>
          {authError ? (
            <Button
              loading={signOut.isPending || signOut.isSuccess ? 'Signing out…' : undefined}
              onClick={() => signOut.mutate()}
            >
              Sign in again
            </Button>
          ) : (
            <Button loading={retrying ? 'Retrying…' : undefined} onClick={onRetry}>
              Try again
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  )
}
